import { default as classNames, default as cn } from 'classnames';
import { API_STATUS } from 'core-lib/constants/network';
import { LocalStorage } from 'core-lib/constants/storage';
import { useAddress } from 'core-lib/hooks/useAddress';
import { useFuelBalance } from 'core-lib/hooks/useFuelBalance';
import { useScroll } from 'core-lib/hooks/useScroll';
import getShareMessage, {
    ShareMessage,
} from 'core-lib/services/getShareMessage';
import {
    handleBack,
    resetWebView,
    share,
    useRedirection,
} from 'core-lib/utils/common';
import { formatFuelBalance } from 'core-lib/utils/fuel';
import { isParkPlusWebView } from 'core-lib/utils/source';
import { isServer } from 'core-lib/utils/utils';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BackButton from 'ui-v2/components/BackButton';

import { AppBar } from 'ui-v2/components/AppBar';

import { Typography } from 'ui-v2/components/Typography';

import toast from 'react-hot-toast';
import Icon from 'ui-v2/components/Icon/Icon';
import { SemanticBgLevels } from 'ui-v2/types/colors';
import parkplusStoreV2RedirectionRoutes from '../../constants/parkplusStoreV2RedirectionRoutes';
import useParkplusStoreV2 from '../../hooks/useParkplusStoreV2';
import { updateDeliveryAddress } from '../../store/action';
import { selectAddressLogic } from '../../utils';
import { trackCtaClickedStoreV2 } from '../../utils/events';
import PetrolBalancePopup from '../Popups/PetrolBalancePopup';
import s from './StoreAppBar.module.scss';

import AddAddressV3 from 'core-lib/components/AddAddressV3';
import { semanticColors } from 'ui-v2/tokens/colors';
import { THEME } from '../../constants';

const StatusBar = dynamic(() => import('core-lib/components/StatusBar'), {
  ssr: false,
});

type StoreAppBarProps = {
  title: string;
  showCart?: boolean;
  showFuel?: boolean;
  bgColor?: SemanticBgLevels;
  balance?: number;
  onBack?: () => void;
  showAddressBar?: boolean;
  renderAppBarOnly?: boolean;
  showShare?: boolean;
  isSticky?: boolean;
  showBackGroundColor?: boolean;
  themeColor?: string;
  statusBarColor?: string;
};

type ActionType = {
  children?: React.ReactElement<HTMLOrSVGElement>;
  onClick?: () => void;
};

export const StoreAppBar: FC<StoreAppBarProps> = ({
  title,
  showFuel = true,
  showCart = false,
  bgColor = 'primary',
  onBack,
  showAddressBar = false,
  renderAppBarOnly = false,
  showShare = false,
  isSticky = false,
  showBackGroundColor = true,
  themeColor = 'light',
  statusBarColor = '#FFFFFF',
}) => {
  //state
  const [toggleClass, setToggleClass] = useState(false);
  const [showAddressV2, setShowAddressV2] = useState(false);
  const [showPetrolpopup, setShowPetrolpopup] = useState(false);
  const [shareMessage, setShareMessage] = useState<ShareMessage>();
  const [onDark, setOnDark] = useState(themeColor === THEME.DARK); // Initialize onDark based on themeColor

  const {
    balance,
    getPetrolBalance,
    apiStatus: fuelBalanceApiStatus,
  } = useFuelBalance();
  const { fetchUserAddresses } = useAddress();

  //redux
  const { addressList, apiStatus } = useSelector(
    (state) => state.auth.userAddresses
  );
  const { isAuthenticated, isStateRestored } = useSelector(
    (state) => state.auth
  );
  const { cartData, deliveryAddressData: address } = useParkplusStoreV2();

  const dispatch = useDispatch();

  const router = useRouter();

  const redirection = useRedirection();

  const { scrollY } = useScroll();

  const onBackClick = () => {
    if (onBack) {
      onBack();
    } else if (router?.asPath === parkplusStoreV2RedirectionRoutes.home) {
      if (isParkPlusWebView()) {
        resetWebView();
      } else {
        router.push('/');
      }
    } else if (router.query.origin_path) {
      redirection?.replace(`${router.query.origin_path}`);
    } else if (router?.query?.page_source === 'order_detail') {
      resetWebView();
    } else {
      handleBack();
    }
  };

  const getShareText = async () => {
    const categoryName = 'store';
    const section = 'store';
    const deeplink = global?.window?.location?.href?.split('?')?.[0];
    const messageParams = {
      deeplink,
    };
    const { response, error } = await getShareMessage(
      categoryName,
      section,
      messageParams
    );
    if (response) {
      setShareMessage(response);
    } else if (error) {
      toast.error(error.message);
    }
  };
  //effects
  useEffect(() => {
    if (
      !isServer() &&
      isAuthenticated &&
      fuelBalanceApiStatus === API_STATUS.default
    ) {
      getPetrolBalance();
    }
  }, [isAuthenticated, fuelBalanceApiStatus]);

  useEffect(() => {
    if (showShare && !shareMessage && isAuthenticated) {
      getShareText();
    }
  }, [showShare, isAuthenticated]);

  useEffect(() => {
    if (
      isAuthenticated &&
      [API_STATUS.default].includes(apiStatus) &&
      !isServer()
    ) {
      fetchUserAddresses();
    }
  }, [isAuthenticated, isStateRestored, apiStatus]);

  useEffect(() => {
    handleAddress();
  }, [addressList]);

  useEffect(() => {
    if (!toggleClass) {
      setToggleClass(true);
      setTimeout(() => {
        setToggleClass(false);
      }, 1000);
    }
  }, [cartData?.data]);

  useEffect(() => {
    if (showAddressBar && address?.selectedAddress) {
      global.localStorage.setItem(
        LocalStorage.PreSelectedAddress,
        JSON.stringify(address?.selectedAddress)
      );
    }
  }, [address?.selectedAddress]);

  const handleScroll = () => {
    const scrollY = window.scrollY;
    setOnDark(scrollY === 0 ? themeColor === THEME.DARK : false);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  //handlers
  const onChangeAddress = (address) => {
    dispatch(
      updateDeliveryAddress({
        data: {
          pincode: address?.pincode,
          serviceable: 1,
          selectedAddress: address,
        },
        apiStatus: API_STATUS.success,
      })
    );
  };

  const handleAddress = async () => {
    if (addressList?.length > 0 && !address?.pincode) {
      const response = await selectAddressLogic(
        addressList,
        address?.selectedAddress
      );
      onChangeAddress(response);
    }
  };

  const onShare = () => {
    share({
      msg: shareMessage.formattedMessage,
      withScreenShot: shareMessage.withScreenshot,
    });
  };

  const getActions = () => {
    let result: ActionType[] = [];

    const count = cartData?.data?.length || 0;

    const renderCartIconWithNumber = () => (
      <div className={s.cart}>
        {count > 0 && (
          <div
            className={cn(s.count_container, {
              [s.rotate_anim]: toggleClass,
            })}
          >
            <div className={s.count}>{count < 100 ? count : '99+'}</div>
          </div>
        )}
        <Icon name="CartRegular" width={20} height={20} />
      </div>
    );

    if (showFuel) {
      result = [
        {
          ...result,
          children: (
            <div
              className={cn(s.chip, {
                ['cmn_shimmer']:
                  fuelBalanceApiStatus === API_STATUS.in_progress,
              })}
              aria-hidden
              onClick={() => {
                setShowPetrolpopup(true);
                trackCtaClickedStoreV2('app_bar', 'fuel_chip');
              }}
            >
              <Icon name="FuelDrop" width={20} height={20} />
              <Typography
                label={balance ? formatFuelBalance(balance) : `0 ml`}
                variant="label_3_semi_bold"
              />
            </div>
          ),
        },
      ];
    }

    if (showShare) {
      result = [
        ...result,
        {
          children: (
            <div style={{ marginTop: -2, padding: '6px' }}>
              <Icon name="ShareRegular" width={24} height={24} />
            </div>
          ),
          onClick: () => {
            trackCtaClickedStoreV2('app_bar', 'share');
            onShare();
          },
        },
      ];
    }
    if (showCart) {
      result = [
        ...result,
        {
          children: renderCartIconWithNumber(),
          onClick: () => {
            trackCtaClickedStoreV2('app_bar', 'cart_icon');
            router.push(parkplusStoreV2RedirectionRoutes.cart);
          },
        },
      ];
    }
    return result;
  };

  return (
    <>
      <div
        className={cn(s.root, {
          [s.sticky]: isSticky,
        })}
        style={{
          background: showBackGroundColor ? semanticColors.bg[bgColor] : null,
        }}
      >
        <StatusBar
          statusBarColor={statusBarColor}
          theme={`${THEME?.DARK}`}
          cleanUpOnUnmount
        />
        {renderAppBarOnly ? (
          <div
            style={
              isSticky
                ? {
                    background: `linear-gradient(to bottom, rgba(255,255,255,${
                      scrollY / 200
                    }) ${
                      Math.min(1, scrollY / 200) * 100
                    }%,rgba(255,255,255,0) 100%)`,
                  }
                : {}
            }
          >
            <AppBar
              isTransparent={!showBackGroundColor}
              title={{ label: title }}
              bgColor={bgColor}
              actions={getActions()}
              zIndex={4}
              onDark={onDark}
              backButton={{
                show: true,
                onClick: () => onBackClick(),
              }}
            />
          </div>
        ) : (
          <div
            className={s.light_bar}
            style={
              isSticky
                ? {
                    background: `linear-gradient(to bottom, rgba(255,255,255,${
                      scrollY / 238
                    }) ${
                      Math.min(1, scrollY / 238) * 100
                    }%,rgba(255,255,255,0) 100%)`,
                  }
                : {}
            }
          >
            <BackButton onBack={onBackClick} type="hidden" />
            <div
              className={cn(s.app_bar_icon, 'cmn_touchable_area')}
              onClick={onBackClick}
              aria-hidden
            >
              <Icon name="ArrowLeftRegular" width={20} height={20} />
            </div>

            <div className={s.items}>
              {getActions().map((item, id) => (
                <div onClick={item.onClick} key={id}>
                  {item?.children}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showAddressBar && address?.selectedAddress?.address?.length > 0 && (
        <div className={s.addressBar}>
          {address?.selectedAddress && (
            <div
              className={s.address}
              onClick={(e) => {
                e.stopPropagation();
                setShowAddressV2(true);
                trackCtaClickedStoreV2('app_bar', 'address');
              }}
            >
              <Icon
                name="LocationBold"
                color={themeColor == THEME.DARK ? 'on_dark_bg' : 'primary'}
                width={20}
                height={20}
              />
              <span className={classNames(s.text, s.addressText)}>
                <Typography
                  label="Deliver to"
                  variant="label_3_medium"
                  color={themeColor == THEME.DARK ? 'on_dark_bg' : 'secondary'}
                />
                &nbsp;
                <Typography
                  label={address?.selectedAddress?.addressTag}
                  variant="label_3_bold"
                  color={themeColor == THEME.DARK ? 'on_dark_bg' : 'primary'}
                />
                &nbsp;
                <Typography
                  label={address?.selectedAddress?.address}
                  variant="label_3_bold"
                  color={themeColor == THEME.DARK ? 'on_dark_bg' : 'primary'}
                />
              </span>
            </div>
          )}
        </div>
      )}

      {showAddressV2 && (
        <>
          <BackButton onBack={() => setShowAddressV2(false)} type="hidden" />
          <AddAddressV3
            isShow
            onClose={() => setShowAddressV2(false)}
            selectedAddress={address?.selectedAddress}
            setSelectedAddress={onChangeAddress}
            checkServiceable
          />
        </>
      )}
      <PetrolBalancePopup
        show={showPetrolpopup}
        onClose={() => {
          setShowPetrolpopup(false);
        }}
        balance={balance}
      />
    </>
  );
};
